"use client";

import { useDeferredValue, useEffect, useMemo, useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  HINT,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  type AnagramResult,
  type AnagramWord,
  anagramSignature,
  loadWordList,
  MAX_TILES,
  MIN_WORD_LENGTH,
  parseRack,
  solveAnagrams,
} from "@/lib/tool-kit/vocabulary/anagram";

/** English strings. A translated content file overrides these through `ui`. */
export const ANAGRAM_SOLVER_STRINGS = {
  lettersLabel: "Your letters",
  lettersPlaceholder: "listen",
  lettersHint:
    "Up to {max} letters. Type ? for a blank tile that can stand for any letter.",
  minLengthLabel: "Shortest word to show",
  lengthOption: "one:{count} letter|other:{count} letters",
  tryLabel: "Try:",
  examples: ["listen", "garden", "retains", "quiz?"],
  emptyPrompt: "Type some letters to see every word they spell.",
  invalid: "Use only the letters A–Z, plus ? for a blank tile.",
  tooLong: "Use at most {max} letters and blanks.",
  loading: "Loading the word list (once per visit)…",
  loadError:
    "Could not load the word list. Check your connection and try again.",
  retry: "Try again",
  tilesLabel: "Your tiles, in alphabetical order",
  heading: "Words from your letters",
  totalCount: "one:{count} word|other:{count} words",
  exactHeading:
    "one:Anagrams using your {count} letter|other:Anagrams using all {count} letters",
  exactNone: "No word uses every tile.",
  groupHeading: "one:{count} letter|other:{count} letters",
  groupCount: "one:{count} word|other:{count} words",
  blankNote: "Underlined letters are spelled with a blank tile.",
  noWords:
    "No words of {min} or more letters can be made from these tiles. Try a lower minimum or add a blank.",
  showAll: "Show all {count}",
  copyAll: "Copy all",
  copied: "Copied!",
  sourceNote:
    "Words from ENABLE, a public-domain word list used by many word games. It is not the official Scrabble dictionary, so a few words differ.",
} as const;

type Strings = typeof ANAGRAM_SOLVER_STRINGS;
type ListState = "idle" | "loading" | "ready" | "error";

const FIELD_ID = "anagram-letters";
const PREVIEW = 120;
const LENGTHS = Array.from(
  { length: MAX_TILES - MIN_WORD_LENGTH + 1 },
  (_, index) => MIN_WORD_LENGTH + index
);

/** The words of one group, with any blank-filled letters underlined. */
function WordList({ words }: { words: readonly AnagramWord[] }) {
  return (
    <ul className="flex flex-wrap gap-2">
      {words.map(({ word, blanks }) => (
        <li
          className="rounded-md border border-border bg-card px-2.5 py-1 font-mono text-foreground text-sm"
          key={word}
        >
          {blanks.length === 0
            ? word
            : Array.from(word).map((char, index) => (
                <span
                  className={
                    blanks.includes(index)
                      ? "text-primary underline decoration-2 underline-offset-4"
                      : undefined
                  }
                  // biome-ignore lint/suspicious/noArrayIndexKey: letters repeat; position is the identity
                  key={index}
                >
                  {char}
                </span>
              ))}
        </li>
      ))}
    </ul>
  );
}

function Group({
  heading,
  count,
  words,
  strings,
}: {
  heading: string;
  count: string;
  words: readonly AnagramWord[];
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? words : words.slice(0, PREVIEW);
  return (
    <section className="space-y-2">
      <h3 className={WIDGET_H3}>
        {heading} <span className={HINT}>{count}</span>
      </h3>
      <WordList words={shown} />
      {words.length > shown.length ? (
        <button
          className={BUTTON_SMALL}
          onClick={() => setExpanded(true)}
          type="button"
        >
          {t(strings.showAll, { count: fmt(words.length) })}
        </button>
      ) : null}
    </section>
  );
}

function Results({
  result,
  size,
  minLength,
  strings,
}: {
  result: AnagramResult;
  size: number;
  minLength: number;
  strings: Strings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  if (result.total === 0) {
    return <Notice>{t(strings.noWords, { min: fmt(minLength) })}</Notice>;
  }
  const entries = [
    ...result.exact,
    ...result.shorter.flatMap((group) => group.words),
  ];
  const all = entries.map((entry) => entry.word);
  const hasBlanks = entries.some((entry) => entry.blanks.length > 0);
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div aria-live="polite" className="space-y-1">
          <h2 className={WIDGET_H2}>{strings.heading}</h2>
          <p className={HINT}>
            {tPlural(strings.totalCount, locale, result.total, {
              count: fmt(result.total),
            })}
          </p>
        </div>
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyAll}
          small
          text={all.join("\n")}
        />
      </div>
      {hasBlanks ? <p className={HINT}>{strings.blankNote}</p> : null}
      <section className="space-y-2">
        <h3 className={WIDGET_H3}>
          {tPlural(strings.exactHeading, locale, size, { count: fmt(size) })}
        </h3>
        {result.exact.length > 0 ? (
          <WordList words={result.exact} />
        ) : (
          <p className="text-muted-foreground text-sm">{strings.exactNone}</p>
        )}
      </section>
      {result.shorter.map((group) => (
        <Group
          count={tPlural(strings.groupCount, locale, group.words.length, {
            count: fmt(group.words.length),
          })}
          heading={tPlural(strings.groupHeading, locale, group.length, {
            count: fmt(group.length),
          })}
          key={group.length}
          strings={strings}
          words={group.words}
        />
      ))}
    </div>
  );
}

export default function AnagramSolverWidget() {
  const strings = useToolUi(ANAGRAM_SOLVER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [input, setInput] = useState("");
  const [minLength, setMinLength] = useState(3);
  const [words, setWords] = useState<string[] | null>(null);
  const [listState, setListState] = useState<ListState>("idle");
  const deferred = useDeferredValue(input);
  const parsed = parseRack(deferred);
  const wanted = parsed.ok;
  const result = useMemo(() => {
    const rack = parseRack(deferred);
    return rack.ok && words ? solveAnagrams(words, rack.rack, minLength) : null;
  }, [deferred, words, minLength]);

  useEffect(() => {
    if (!wanted || listState !== "idle") {
      return;
    }
    setListState("loading");
    loadWordList()
      .then((list) => {
        setWords(list);
        setListState("ready");
      })
      .catch(() => setListState("error"));
  }, [wanted, listState]);

  function notice() {
    if (parsed.ok) {
      return null;
    }
    if (parsed.reason === "invalid") {
      return <ErrorNotice>{strings.invalid}</ErrorNotice>;
    }
    if (parsed.reason === "too-long") {
      return (
        <ErrorNotice>{t(strings.tooLong, { max: fmt(MAX_TILES) })}</ErrorNotice>
      );
    }
    return (
      <Notice>
        <span className="me-2">{strings.emptyPrompt}</span>
        <span className="me-2">{strings.tryLabel}</span>
        {strings.examples.map((example, index) => (
          <span key={example}>
            {index > 0 ? " · " : null}
            <button
              className="font-medium font-mono text-primary underline-offset-4 hover:underline"
              onClick={() => setInput(example)}
              type="button"
            >
              {example}
            </button>
          </span>
        ))}
      </Notice>
    );
  }

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-[2fr_1fr]">
        <TextField
          hint={t(strings.lettersHint, { max: fmt(MAX_TILES) })}
          id={FIELD_ID}
          label={strings.lettersLabel}
          mono
          onChange={(value) => setInput(value.slice(0, MAX_TILES * 2))}
          placeholder={strings.lettersPlaceholder}
          type="search"
          value={input}
        />
        <SelectField
          id="anagram-min"
          label={strings.minLengthLabel}
          onChange={(value) => setMinLength(Number(value))}
          options={LENGTHS.map((length) => ({
            value: String(length),
            label: tPlural(strings.lengthOption, locale, length, {
              count: fmt(length),
            }),
          }))}
          value={String(minLength)}
        />
      </div>
      {parsed.ok ? (
        <div className="space-y-2">
          <p className={HINT}>{strings.tilesLabel}</p>
          <p className="flex flex-wrap gap-1.5">
            {Array.from(anagramSignature(parsed.rack.text)).map(
              (tile, index) => (
                <span
                  className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-amber-300 bg-amber-100 font-bold text-amber-900 uppercase dark:border-amber-700 dark:bg-amber-900/40 dark:text-amber-100"
                  // biome-ignore lint/suspicious/noArrayIndexKey: tiles repeat; position is the identity
                  key={index}
                >
                  {tile === "?" ? "" : tile}
                </span>
              )
            )}
          </p>
        </div>
      ) : null}
      {notice()}
      {parsed.ok && listState === "loading" ? (
        <output className="block text-muted-foreground text-sm">
          {strings.loading}
        </output>
      ) : null}
      {parsed.ok && listState === "error" ? (
        <div className="space-y-3">
          <ErrorNotice>{strings.loadError}</ErrorNotice>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => setListState("idle")}
            type="button"
          >
            {strings.retry}
          </button>
        </div>
      ) : null}
      {parsed.ok && result ? (
        <Results
          minLength={minLength}
          result={result}
          size={parsed.rack.size}
          strings={strings}
        />
      ) : null}
      <p className={HINT}>{strings.sourceNote}</p>
    </>
  );
}
